import { useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthStore } from '@features/authentication/store/authStore';
import { playArrivalTone } from '../lib/notificationSound';
import { readSoundEnabled, writeSoundEnabled } from '../lib/soundPrefs';

/**
 * Mute or unmute the arrival tones.
 *
 * <b>Icon-only, not a labelled switch like `DesktopPushToggle` beside it.</b> The footer is 264px
 * wide and already spends most of it on "Desktop notifications" and "See all"; a second labelled row
 * does not fit. Icon-only means it MUST carry a Tooltip and a matching `aria-label` (UI gate §5),
 * and both are written to name the ACTION rather than the state, because the glyph already shows the
 * state.
 *
 * <b>⚠️ Turning it ON plays the tone, and that is load-bearing, not a flourish.</b> Browsers refuse
 * audio until the page has been interacted with, so a freshly loaded tab is muted by the platform no
 * matter what this preference says. A click IS that interaction — so the preview both confirms the
 * choice and unlocks the `AudioContext` for the rest of the session. Without it the first real
 * notification of the day would silently do nothing and read as a broken feature.
 */
export function NotificationSoundToggle() {
  const userId = useAuthStore((s) => s.user?.userId ?? null);

  // Read once into local state: the value lives in localStorage, which is not reactive, and this is
  // the only thing in the app that writes it.
  const [enabled, setEnabled] = useState(() => readSoundEnabled(userId));

  const label = enabled ? 'Turn notification sound off' : 'Turn notification sound on';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-pressed={enabled}
          className="h-7 w-7 flex-shrink-0"
          onClick={() => {
            const next = !enabled;
            setEnabled(next);
            writeSoundEnabled(userId, next);
            if (next) playArrivalTone('info');
          }}
        >
          {enabled ? (
            <Volume2 className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <VolumeX className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}
